/**
 * Document Review Screen
 *
 * T095 [US5]: Screen for reviewing AI-extracted positions and trades before saving.
 * Displays extracted data with confidence scores, allows editing, and saves to portfolio.
 * NOW THEME-AWARE
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  BackHandler,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import {
  useDocumentStatus,
  useExtractedData,
  useSaveExtractedData,
  useCancelDocumentImport,
} from '../../services/api/queries/useDocumentImport';
import { ExtractedPositionsList } from '../../components/document-review/ExtractedPositionsList';
import { showSuccess, showError } from '../../utils/alert';
import { validateExtractedData, normalizeSymbol } from '../../utils/validation';
import { LoadingModal } from '../../components/ui/LoadingModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useDocumentImportContext } from '../../contexts/DocumentImportContext';
import { useTranslation } from 'react-i18next';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';
import type {
  ExtractedDataResponse,
  ExtractedPosition,
  ExtractedTrade,
} from '../../services/api/documentImportApi';
import { getPositions, getTrades, type Position, type Trade } from '../../services/portfolioApi';

type DocumentReviewScreenNavigationProp = StackNavigationProp<
  PortfolioNavigatorParamList,
  'DocumentReview'
>;
type DocumentReviewScreenRouteProp = RouteProp<PortfolioNavigatorParamList, 'DocumentReview'>;

function DocumentReviewScreenWithId({
  importId,
  extractedDataFromParams,
  failedImages,
}: {
  importId: number;
  extractedDataFromParams?: ExtractedDataResponse | null;
  failedImages?: string[];
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { activeImport, clearImport } = useDocumentImportContext();

  const navigation = useNavigation<DocumentReviewScreenNavigationProp>();
  const route = useRoute<DocumentReviewScreenRouteProp>();

  // Clear the global import context when navigating to this screen
  // This dismisses the banner/loading bar since the user is now on the review screen
  useEffect(() => {
    if (activeImport && activeImport.importId === importId) {
      clearImport();
    }
  }, [importId, activeImport, clearImport]);

  // T100 [US5]: Poll document status during processing
  const {
    data: status,
    isLoading: isLoadingStatus,
    error: statusError,
  } = useDocumentStatus(importId);

  // Only fetch from server if data wasn't passed in params
  const shouldFetchData = !extractedDataFromParams;
  const {
    data: extractedDataFromServer,
    isLoading: isLoadingData,
    error: dataError,
  } = useExtractedData(
    importId,
    shouldFetchData && status?.status === 'complete' // Only fetch when complete and not passed in params
  );

  // Use data from params if available, otherwise use data from server
  const extractedData = extractedDataFromParams || extractedDataFromServer;

  const saveMutation = useSaveExtractedData();
  const cancelMutation = useCancelDocumentImport();

  const [editedPositions, setEditedPositions] = useState<ExtractedPosition[]>([]);
  const [editedTrades, setEditedTrades] = useState<ExtractedTrade[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [existingPositions, setExistingPositions] = useState<Position[]>([]);
  const [existingTrades, setExistingTrades] = useState<Trade[]>([]);

  // Fetch existing positions and trades when component mounts
  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        console.log('[DocumentReviewScreen] Fetching existing positions and trades...');
        const positions = await getPositions();
        console.log(`[DocumentReviewScreen] Fetched ${positions.length} existing positions`);
        setExistingPositions(positions);
        const trades = await getTrades();
        console.log(`[DocumentReviewScreen] Fetched ${trades.length} existing trades`);
        setExistingTrades(trades);
      } catch (error) {
        console.error('Failed to fetch existing data:', error);
      }
    };
    fetchExistingData();
  }, []);

  // Update edited data when extraction completes and detect duplicates
  useEffect(() => {
    console.log(
      `[DocumentReviewScreen] useEffect triggered - extractedData positions: ${extractedData?.positions?.length || 0}, existingPositions: ${existingPositions.length}, existingTrades: ${existingTrades.length}`
    );

    if (extractedData?.positions) {
      let positions = extractedData.positions;

      console.log(
        `[DocumentReviewScreen] Processing ${positions.length} extracted positions. Existing positions count: ${existingPositions.length}`
      );

      // Detect duplicates if we have existing positions loaded
      if (existingPositions.length > 0) {
        positions = positions.map((pos) => {
          // Normalize both symbols for comparison
          const normalizedPosSymbol = normalizeSymbol(pos.symbol || '');

          // Find matching existing position by normalized symbol
          // Note: Currency is not used for matching since it may be missing or inconsistent
          let foundMatch = false;
          const existing = existingPositions.find((ep) => {
            const normalizedEpSymbol = normalizeSymbol(ep.symbol || '');
            const match = normalizedEpSymbol === normalizedPosSymbol;

            // Log each comparison for debugging
            if (match) {
              console.log(
                `[DocumentReviewScreen] ✓ Checking ${pos.symbol} (normalized: ${normalizedPosSymbol}) vs ${ep.symbol} (normalized: ${normalizedEpSymbol}): MATCH (currency: extracted=${pos.currency}, existing=${ep.currency})`
              );
              foundMatch = true;
            }

            return match;
          });

          if (existing) {
            // Mark as duplicate with existing position info
            console.log(
              `[DocumentReviewScreen] ✓ Detected duplicate position: ${pos.symbol} matches existing ${existing.symbol} (ID: ${existing.id})`
            );
            return {
              ...pos,
              is_duplicate: true,
              existing_position_id: existing.id,
            };
          } else {
            console.log(
              `[DocumentReviewScreen] ✗ No match found for ${pos.symbol} (normalized: ${normalizedPosSymbol})`
            );
          }
          return pos;
        });

        const duplicateCount = positions.filter((p) => p.is_duplicate).length;
        console.log(
          `[DocumentReviewScreen] Position duplicate detection complete: ${duplicateCount}/${positions.length} duplicates found`
        );
      } else {
        console.log(
          '[DocumentReviewScreen] Skipping position duplicate detection - no existing positions loaded yet'
        );
      }

      setEditedPositions(positions);
    }
    if (extractedData?.trades) {
      let trades = extractedData.trades;

      console.log(
        `[DocumentReviewScreen] Processing ${trades.length} trades with ${existingTrades.length} existing trades`
      );

      // Detect duplicate trades within the same batch and against existing trades
      // Database unique constraint: (user_id, symbol, trade_datetime, quantity, trade_price)
      const tradeKeys = new Map<string, number>(); // Tracks first occurrence of each unique trade

      trades = trades.map((trade, index) => {
        // First check against existing trades in portfolio
        if (existingTrades.length > 0) {
          const existing = existingTrades.find((et) => {
            // Match on: symbol, trade_datetime, quantity, trade_price
            const tradeDate = trade.date ? trade.date : '';
            const existingDate = et.trade_datetime ? et.trade_datetime.split('T')[0] : '';

            console.log(
              `[DocumentReviewScreen] Checking trade ${index}: symbol=${trade.symbol}, tradeDate="${tradeDate}", existingDate="${existingDate}", quantity=${Math.abs(trade.quantity)}, trade_price=${trade.price}, et.trade_price=${et.trade_price}`
            );

            // Compare all four fields that make up the unique constraint
            return (
              et.symbol === trade.symbol &&
              existingDate === tradeDate &&
              Math.abs(et.quantity) === Math.abs(trade.quantity) &&
              Math.round(et.trade_price * 1000) === Math.round((trade.price || 0) * 1000) // Round to avoid floating point issues
            );
          });

          if (existing) {
            console.log(
              `[DocumentReviewScreen] ✓ DETECTED DUPLICATE: ${trade.symbol} at ${trade.date} (existing trade ID: ${existing.id})`
            );
            return {
              ...trade,
              is_duplicate: true,
            };
          } else {
            console.log(`[DocumentReviewScreen] ✗ NOT duplicate: ${trade.symbol} at ${trade.date}`);
          }
        }

        // Then check for duplicates within the same batch
        const key = `${trade.symbol}_${trade.price || 0}_${trade.date || ''}_${trade.quantity}`;

        if (tradeKeys.has(key)) {
          // This is a duplicate - mark it
          const firstIndex = tradeKeys.get(key)!;
          console.log(
            `[DocumentReviewScreen] ✓ DUPLICATE WITHIN BATCH: ${key} at index ${index} (first seen at index ${firstIndex})`
          );
          return {
            ...trade,
            is_duplicate: true,
          };
        } else {
          // First occurrence - track it
          tradeKeys.set(key, index);
          return trade;
        }
      });

      const duplicateCount = trades.filter((t) => t.is_duplicate).length;
      console.log(
        `[DocumentReviewScreen] Final duplicate count: ${duplicateCount}/${trades.length}`
      );

      setEditedTrades(trades);
    }
  }, [extractedData, existingPositions, existingTrades]);

  // Re-check for duplicate trades when trades are modified
  // Use a ref to prevent infinite loops
  const isRecheckingRef = React.useRef(false);

  useEffect(() => {
    // Skip if we're already re-checking to prevent infinite loops
    if (isRecheckingRef.current || editedTrades.length === 0) {
      isRecheckingRef.current = false;
      return;
    }

    // Detect duplicate trades within the same batch and against existing trades
    // Database unique constraint: (user_id, symbol, trade_datetime, quantity, trade_price)
    const tradeKeys = new Map<string, number>(); // Tracks first occurrence of each unique trade

    const updatedTrades = editedTrades.map((trade, index) => {
      // First check against existing trades in portfolio
      let isDuplicateOfExisting = false;
      if (existingTrades.length > 0) {
        const existing = existingTrades.find((et) => {
          // Match on: symbol, trade_datetime, quantity, trade_price
          const tradeDate = trade.date ? trade.date : '';
          const existingDate = et.trade_datetime ? et.trade_datetime.split('T')[0] : '';

          return (
            et.symbol === trade.symbol &&
            existingDate === tradeDate &&
            Math.abs(et.quantity) === Math.abs(trade.quantity) &&
            Math.round(et.trade_price * 1000) === Math.round((trade.price || 0) * 1000)
          );
        });

        if (existing) {
          console.log(
            `[DocumentReviewScreen] Re-detected duplicate trade with existing: ${trade.symbol} at ${trade.date} (existing ID: ${existing.id})`
          );
          isDuplicateOfExisting = true;
        }
      }

      // Then check for duplicates within the same batch
      const key = `${trade.symbol}_${trade.price || 0}_${trade.date || ''}_${trade.quantity}`;
      let isDuplicateWithinBatch = false;

      if (tradeKeys.has(key)) {
        // This is a duplicate within the batch
        const firstIndex = tradeKeys.get(key)!;
        console.log(
          `[DocumentReviewScreen] Re-detected duplicate trade within batch: ${key} at index ${index} (first at ${firstIndex})`
        );
        isDuplicateWithinBatch = true;
      } else {
        // First occurrence - track it
        tradeKeys.set(key, index);
      }

      // Set duplicate flag if duplicate of existing OR duplicate within batch
      const newDuplicateFlag = isDuplicateOfExisting || isDuplicateWithinBatch;

      // Only update if flag changed
      if (trade.is_duplicate !== newDuplicateFlag) {
        return {
          ...trade,
          is_duplicate: newDuplicateFlag,
        };
      }

      return trade; // No change
    });

    // Only update if something actually changed
    const hasChanges = updatedTrades.some(
      (t, i) => t.is_duplicate !== editedTrades[i].is_duplicate
    );
    if (hasChanges) {
      isRecheckingRef.current = true;
      setEditedTrades(updatedTrades);
    }
  }, [
    editedTrades
      .map((t) => `${t.symbol}_${t.price}_${t.date}_${t.quantity}_${t.is_duplicate}`)
      .join(','),
    existingTrades,
  ]);

  // Show warning for failed images from multi-image import
  useEffect(() => {
    if (failedImages && failedImages.length > 0) {
      Alert.alert(
        t('portfolio.someImagesFailed', { defaultValue: 'Some Images Failed' }),
        t('portfolio.imagesFailedToProcess', {
          defaultValue:
            '{{count}} image(s) failed to process:\n\n{{list}}{{more}}\n\nThe remaining images were processed successfully.',
          count: failedImages.length,
          list: failedImages.slice(0, 5).join('\n'),
          more: failedImages.length > 5 ? `\n... and ${failedImages.length - 5} more` : '',
        }),
        [{ text: t('common.ok', { defaultValue: 'OK' }) }]
      );
    }
  }, [failedImages, t]);

  const handleSave = async () => {
    if (editedPositions.length === 0 && editedTrades.length === 0) {
      Alert.alert(
        t('portfolio.noData', { defaultValue: 'No Data' }),
        t('portfolio.noPositionsOrTradesToSave', {
          defaultValue: 'There are no positions or trades to save.',
        })
      );
      return;
    }

    // Check for unresolved duplicate positions
    const unresolvedDuplicates = editedPositions.filter(
      (p) => p.is_duplicate && !p.resolution_action
    );

    if (unresolvedDuplicates.length > 0) {
      showError(
        t('portfolio.duplicatePositionsWarning', {
          defaultValue:
            'You have {{count}} duplicate position(s). Please select "Add" or "Replace" for each duplicate position.',
          count: unresolvedDuplicates.length,
        })
      );
      return;
    }

    // Check for duplicate trades - prevent save until duplicates are removed or updated
    const duplicateTrades = editedTrades.filter((t) => t.is_duplicate);

    if (duplicateTrades.length > 0) {
      showError(
        t('portfolio.duplicateTradesWarning', {
          defaultValue:
            'You have {{count}} duplicate trade(s). Remove duplicates or update their details (symbol, price, date, or quantity).',
          count: duplicateTrades.length,
        })
      );
      return;
    }

    // T085 [US5]: Validate all extracted data before saving
    const validationErrors = validateExtractedData(editedPositions, editedTrades);

    if (validationErrors.length > 0) {
      // Show validation errors to user
      Alert.alert(
        t('portfolio.validationErrors', { defaultValue: 'Validation Errors' }),
        t('portfolio.correctErrorsBeforeSaving', {
          defaultValue: 'Please correct the following errors before saving:\n\n{{errors}}{{more}}',
          errors: validationErrors.slice(0, 5).join('\n'),
          more: validationErrors.length > 5 ? `\n... and ${validationErrors.length - 5} more` : '',
        })
      );
      return;
    }

    setIsSaving(true);

    try {
      // Log the data being sent for debugging
      console.log(
        '[DocumentReviewScreen] Saving positions:',
        JSON.stringify(editedPositions, null, 2)
      );

      const result = await saveMutation.mutateAsync({
        importId,
        data: {
          positions: editedPositions,
          trades: editedTrades,
        },
      });

      // Check if any duplicates were skipped
      if (result.duplicates_skipped && result.duplicates_skipped.length > 0) {
        const duplicateList = result.duplicates_skipped.join(', ');
        Alert.alert(
          t('portfolio.dataSavedWithDuplicates', { defaultValue: 'Data Saved with Duplicates' }),
          t('portfolio.skippedDuplicateTrades', {
            defaultValue:
              'Successfully saved {{positions}} positions and {{trades}} trades.\n\nSkipped {{skipped}} duplicate trades that already exist:\n{{list}}',
            positions: result.positions_created,
            trades: result.trades_created,
            skipped: result.duplicates_skipped.length,
            list: duplicateList,
          }),
          [{ text: t('common.ok', { defaultValue: 'OK' }), onPress: () => navigation.goBack() }]
        );
      } else {
        showSuccess(
          t('portfolio.dataSavedSuccessfully', { defaultValue: 'Data saved successfully!' })
        );
        navigation.goBack();
      }
    } catch (error) {
      console.error('Save failed:', error);
      showError(
        error instanceof Error
          ? error.message
          : t('portfolio.failedToSaveData', { defaultValue: 'Failed to save data' })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = useCallback(async () => {
    // T128 [US8]: Cancel document import and discard extracted data
    try {
      await cancelMutation.mutateAsync(importId);
      showSuccess(t('portfolio.processingCancelled', { defaultValue: 'Processing cancelled' }));
    } catch (error) {
      console.error('Failed to cancel:', error);
      // Navigate back anyway even if cancel API fails
    } finally {
      navigation.goBack();
    }
  }, [importId, cancelMutation, navigation]);

  // T136 [US8]: Check if there are low-confidence extractions
  const hasLowConfidenceExtractions = React.useMemo(() => {
    const lowConfidencePositions = editedPositions.filter(
      (p) => p.confidence !== undefined && p.confidence < 0.7
    );
    const lowConfidenceTrades = editedTrades.filter(
      (t) => t.confidence !== undefined && t.confidence < 0.7
    );
    return lowConfidencePositions.length > 0 || lowConfidenceTrades.length > 0;
  }, [editedPositions, editedTrades]);

  // Check if there are unresolved duplicates
  const hasUnresolvedDuplicates = React.useMemo(() => {
    return editedPositions.some((p) => p.is_duplicate && !p.resolution_action);
  }, [editedPositions]);

  // Block Android back button during processing (not applicable on web)
  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS === 'web') {
        return; // BackHandler not supported on web
      }

      const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
        // Show confirmation if processing
        if (isLoadingStatus || status?.status === 'processing') {
          Alert.alert(
            t('portfolio.cancelProcessing', { defaultValue: 'Cancel Processing?' }),
            t('portfolio.cancelAiProcessing', {
              defaultValue: 'Do you want to cancel the AI processing?',
            }),
            [
              { text: t('common.no', { defaultValue: 'No' }), style: 'cancel' },
              {
                text: t('common.yes', { defaultValue: 'Yes' }),
                onPress: () => {
                  handleCancel();
                  return true;
                },
              },
            ]
          );
          return true; // Prevent default back behavior
        }
        return false;
      });

      return () => subscription.remove();
    }, [isLoadingStatus, status?.status, handleCancel])
  );

  // Determine if we should show loading modal
  const isLoadingModal = isLoadingStatus || status?.status === 'processing';

  // Determine if we should show error state
  const hasError = status?.status === 'failed' || dataError || statusError;

  // Determine if we have no data
  const hasNoData =
    !isLoadingStatus &&
    !hasError &&
    !isLoadingData &&
    (!extractedData || (extractedData.positions.length === 0 && extractedData.trades.length === 0));

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* T100 [US5]: Show loading modal during AI processing - rendered as overlay */}
      <LoadingModal
        visible={isLoadingModal}
        title={t('portfolio.processingDocument', { defaultValue: 'Processing Document' })}
        message={t('portfolio.analyzingDocument', {
          defaultValue: 'Analyzing your document with AI...',
        })}
        subMessage={t('portfolio.mayTakeFewMoments', {
          defaultValue: 'This may take a few moments',
        })}
        onCancel={handleCancel}
        showCancelButton={true}
      />

      {/* Error state */}
      {hasError ? (
        <View style={[styles.centerContainer, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.errorTitle, { color: theme.colors.error }]}>
            {t('portfolio.processingFailed', { defaultValue: 'Processing Failed' })}
          </Text>
          <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>
            {status?.error_message ||
              dataError?.message ||
              statusError?.message ||
              t('portfolio.unknownError', { defaultValue: 'Unknown error' })}
          </Text>
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[
                styles.button,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={handleCancel}
            >
              <Text style={[styles.buttonText, { color: theme.colors.text }]}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : isLoadingData ? (
        // Loading while fetching extracted data
        <View style={[styles.centerContainer, { backgroundColor: theme.colors.surface }]}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
            {t('portfolio.loadingExtractedData', { defaultValue: 'Loading extracted data...' })}
          </Text>
        </View>
      ) : hasNoData ? (
        // No data extracted
        <View style={[styles.centerContainer, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.errorTitle, { color: theme.colors.error }]}>
            {t('portfolio.noDataFound', { defaultValue: 'No Data Found' })}
          </Text>
          <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>
            {t('portfolio.couldNotExtractData', {
              defaultValue: 'Could not extract any positions or trades from the document.',
            })}
          </Text>
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[
                styles.button,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={handleCancel}
            >
              <Text style={[styles.buttonText, { color: theme.colors.text }]}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        // Normal content - show extracted data for review
        <>
          <ScrollView style={styles.scrollView}>
            {/* Confidence scores */}
            {extractedData?.confidence_scores && (
              <View
                style={[
                  styles.confidenceBar,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderBottomColor: theme.colors.border,
                  },
                ]}
              >
                <Text style={[styles.confidenceText, { color: theme.colors.primary }]}>
                  {t('portfolio.aiConfidence', {
                    defaultValue: 'AI Confidence: Positions {{pos}}% | Trades {{trades}}%',
                    pos: ((extractedData.confidence_scores.positions ?? 0) * 100).toFixed(0),
                    trades: ((extractedData.confidence_scores.trades ?? 0) * 100).toFixed(0),
                  })}
                </Text>
              </View>
            )}

            {/* T136 [US8]: Low-confidence warning */}
            {hasLowConfidenceExtractions && (
              <View
                style={[
                  styles.warningBar,
                  {
                    backgroundColor: theme.colors.warning + '20',
                    borderBottomColor: theme.colors.warning || '#ffc107',
                  },
                ]}
              >
                <Text style={[styles.warningText, { color: theme.colors.warning || '#856404' }]}>
                  ⚠️{' '}
                  {t('portfolio.lowConfidenceWarning', {
                    defaultValue:
                      'Some extractions have low confidence. Please review carefully before saving.',
                  })}
                </Text>
              </View>
            )}

            {/* Unresolved duplicates warning */}
            {hasUnresolvedDuplicates && (
              <View
                style={[
                  styles.duplicateWarningBar,
                  {
                    backgroundColor: theme.colors.error + '20',
                    borderBottomColor: theme.colors.error,
                  },
                ]}
              >
                <Text style={[styles.warningText, { color: theme.colors.error }]}>
                  ⚠️{' '}
                  {t('portfolio.unresolvedDuplicatesWarning', {
                    defaultValue:
                      'Some positions already exist in your portfolio. Please resolve them before saving.',
                  })}
                </Text>
              </View>
            )}

            {/* T096 [US5]: Extracted positions list */}
            {editedPositions.length > 0 && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {t('portfolio.positions', { defaultValue: 'Positions' })} (
                  {editedPositions.length})
                </Text>
                <ExtractedPositionsList
                  positions={editedPositions}
                  onPositionsChange={setEditedPositions}
                />
              </View>
            )}

            {/* Extracted trades list */}
            {editedTrades.length > 0 && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {t('portfolio.trades', { defaultValue: 'Trades' })} ({editedTrades.length})
                </Text>
                <ExtractedPositionsList
                  positions={editedTrades}
                  onPositionsChange={setEditedTrades}
                />
              </View>
            )}
          </ScrollView>

          {/* Action buttons */}
          <View
            style={[
              styles.footer,
              { backgroundColor: theme.colors.surface, borderTopColor: theme.colors.border },
            ]}
          >
            <TouchableOpacity
              style={[
                styles.button,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={handleCancel}
              disabled={isSaving}
            >
              <Text style={[styles.buttonText, { color: theme.colors.text }]}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color={theme.colors.textInverse} />
              ) : (
                <Text style={[styles.buttonText, { color: theme.colors.textInverse }]}>
                  {t('portfolio.saveToPortfolio', { defaultValue: 'Save to Portfolio' })}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollView: {
      flex: 1,
    },
    centerContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    loadingText: {
      marginTop: 16,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightMedium,
    },
    subText: {
      marginTop: theme.spacing.sm,
      fontSize: theme.typography.fontSizeSm,
    },
    errorTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
    },
    errorMessage: {
      fontSize: theme.typography.fontSizeMd,
      textAlign: 'center',
      marginBottom: 24,
    },
    confidenceBar: {
      padding: theme.spacing.md,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    confidenceText: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
    },
    warningBar: {
      padding: theme.spacing.md,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    warningText: {
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
    },
    duplicateWarningBar: {
      padding: theme.spacing.md,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    section: {
      marginTop: 16,
      paddingHorizontal: theme.spacing.md,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.md,
    },
    footer: {
      flexDirection: 'row',
      padding: theme.spacing.md,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
      gap: theme.spacing.md,
    },
    button: {
      flex: 1,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
    },
    cancelButton: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    saveButton: {},
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}

/** Guard wrapper: owns the missing-importId early return so the inner
 * component can call its hooks unconditionally (rules-of-hooks). */
export function DocumentReviewScreen() {
  const { t } = useTranslation();
  const navigation = useNavigation<DocumentReviewScreenNavigationProp>();
  const route = useRoute<DocumentReviewScreenRouteProp>();
  const importId = route.params?.importId;

  if (!importId) {
    showError(t('portfolio.missingDocumentId', { defaultValue: 'Missing document ID' }));
    navigation.goBack();
    return null;
  }

  return (
    <DocumentReviewScreenWithId
      importId={importId}
      extractedDataFromParams={route.params?.extractedData}
      failedImages={route.params?.failedImages}
    />
  );
}
